import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Download, Upload, Trash2, FileText, Plus, Edit3, Save } from "lucide-react";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.doc,.docx";
const INPUT_CLS = "w-full bg-transparent border border-white/15 focus:border-[#A855F7] outline-none text-white p-2 text-sm";
const multipart = { headers: { "Content-Type": "multipart/form-data" } };

async function downloadAttachment(att) {
  try {
    const r = await api.get(`/regulation/attachments/${att.id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: att.content_type || "application/octet-stream" }));
    const a = document.createElement("a");
    a.href = url; a.download = att.filename || "allegato";
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) { toast.error("Download non riuscito"); }
}

function SectionTextEditor({ reg, onChange, onClose }) {
  const [title, setTitle] = useState(reg.attachments_title);
  const [description, setDescription] = useState(reg.attachments_description || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!title.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/regulation/attachments-section", { title, description });
      onChange(data); onClose(); toast.success("Sezione aggiornata");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="space-y-3 mb-4">
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Titolo sezione</span>
        <input data-testid="att-title-input" value={title} maxLength={120} onChange={e => setTitle(e.target.value)} className={INPUT_CLS} />
      </label>
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-white/40 block mb-1">Testo descrittivo</span>
        <textarea data-testid="att-description-input" rows={4} value={description} onChange={e => setDescription(e.target.value)} className={INPUT_CLS} />
      </label>
      <div className="flex gap-2">
        <button data-testid="att-text-save-btn" disabled={saving} onClick={save} className="btn-primary flex items-center gap-2"><Save size={14} /> {saving ? "Salvo…" : "Salva"}</button>
        <button data-testid="att-text-cancel-btn" onClick={onClose} className="btn-ghost">Annulla</button>
      </div>
    </div>
  );
}

function AttachmentRow({ att, isAdmin, busy, onReplace, onDelete }) {
  return (
    <div data-testid={`att-row-${att.id}`} className="flex flex-col md:flex-row md:items-center justify-between gap-3 py-3">
      <div className="flex items-center gap-2 min-w-0 text-sm">
        <FileText size={16} className="text-[#A855F7] shrink-0" />
        <span data-testid={`att-filename-${att.id}`} className="truncate">{att.filename}</span>
        <span className="text-white/40 text-xs shrink-0">{new Date(att.uploaded_at).toLocaleDateString("it-IT")}</span>
      </div>
      <div className="flex gap-2 flex-wrap">
        <button data-testid={`att-download-${att.id}`} onClick={() => downloadAttachment(att)} className="btn-primary !py-1.5 !text-[11px] flex items-center gap-1"><Download size={12} /> Scarica</button>
        {isAdmin && (
          <>
            <button data-testid={`att-replace-${att.id}`} disabled={busy} onClick={() => onReplace(att)} className="btn-ghost !py-1.5 !text-[11px] flex items-center gap-1"><Upload size={12} /> Sostituisci</button>
            <button data-testid={`att-delete-${att.id}`} disabled={busy} onClick={() => onDelete(att)} className="text-[#FF3366]/80 hover:text-[#FF3366] text-xs flex items-center gap-1 px-2"><Trash2 size={12} /> Elimina</button>
          </>
        )}
      </div>
    </div>
  );
}

export function RegulationAttachments({ reg, onChange, isAdmin }) {
  const inputRef = useRef(null);
  const replaceTarget = useRef(null);
  const [busy, setBusy] = useState(false);
  const [editText, setEditText] = useState(false);
  const files = reg.attachments || [];

  const pick = (att) => { replaceTarget.current = att; inputRef.current?.click(); };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const target = replaceTarget.current;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = target
        ? await api.put(`/regulation/attachments/${target.id}`, fd, multipart)
        : await api.post("/regulation/attachments", fd, multipart);
      onChange(data);
      toast.success(target ? "File sostituito" : "File caricato");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); replaceTarget.current = null; }
  };
  const remove = async (att) => {
    if (!window.confirm(`Eliminare "${att.filename}"?`)) return;
    setBusy(true);
    try { const { data } = await api.delete(`/regulation/attachments/${att.id}`); onChange(data); toast.success("File eliminato"); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (!isAdmin && !files.length && !reg.attachments_description) return null;

  return (
    <div data-testid="reg-attachments-section" className="card-surface p-4 md:p-6 mt-6">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <h3 data-testid="att-title" className="font-heading font-black uppercase tracking-tight text-xl text-[#A855F7]">{reg.attachments_title}</h3>
        {isAdmin && !editText && (
          <button data-testid="att-edit-text-btn" onClick={() => setEditText(true)} className="btn-ghost !py-1.5 !text-[11px] flex items-center gap-1"><Edit3 size={12} /> Modifica testo</button>
        )}
      </div>
      {editText ? (
        <SectionTextEditor reg={reg} onChange={onChange} onClose={() => setEditText(false)} />
      ) : (
        reg.attachments_description && <p data-testid="att-description" className="text-white/80 text-sm whitespace-pre-line mb-3">{reg.attachments_description}</p>
      )}
      {isAdmin && <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={onFile} data-testid="att-file-input" />}
      <div className="divide-y divide-white/5">
        {files.map(a => <AttachmentRow key={a.id} att={a} isAdmin={isAdmin} busy={busy} onReplace={pick} onDelete={remove} />)}
      </div>
      {!files.length && <div data-testid="att-empty" className="text-sm text-white/50 py-2">Nessun file allegato.</div>}
      {isAdmin && (
        <button data-testid="att-upload-btn" disabled={busy} onClick={() => pick(null)} className="btn-primary !py-1.5 !text-[11px] flex items-center gap-1 mt-3">
          <Plus size={12} /> {busy ? "Caricamento…" : "Carica file"}
        </button>
      )}
      {isAdmin && <div className="text-[10px] uppercase tracking-widest text-white/30 mt-2">PDF, JPG, PNG, DOC, DOCX · max 20 MB</div>}
    </div>
  );
}
